/**
 * WHICH COMPARABLES BUILT THIS ARV, AND HOW FIRM THAT MAKES IT.
 *
 * Two statements that the Results screen used to get wrong in opposite directions, pulled
 * out of the 6,000-line page so they can be tested as arithmetic instead of as a DOM.
 *
 * 1. WHOSE EVIDENCE. `sales_method` describes the SHAPE of the calculation, not where the
 *    evidence came from, so an ARV built entirely from three comparables the user added
 *    still comes back labelled `floor_anchored_no_livable_comps` — and the screen told
 *    that user "No livable comparable sales or listings nearby" seconds after the engine
 *    had valued their property on the comps they supplied. Measured on 5060 Tireman:
 *    three user comps at $81-90/sqft moved the ARV from $192,400 to $173,237, which is
 *    their own band, and the screen denied it had happened. With the editable ARV removed,
 *    adding comparables is the user's only way to change the number; it cannot end by
 *    telling them their work did nothing.
 *
 * 2. HOW FIRM. The engine already computes the 3-comp threshold and nobody reads it:
 *    `check_data_coverage` raises LOW_COMP_COUNT and sets valuation_confidence 'low'
 *    below 3 qualified comps, and that verdict has never reached a screen. This publishes
 *    it as CONFIDENCE, never as permission — no button is disabled by it, because one
 *    comparable the investor knows first-hand can be worth more than three the system
 *    found. It counts the TOTAL behind the ARV, not the user's share: telling someone
 *    whose ARV rests on 5 automatic comps plus 2 of theirs that it would be firmer with
 *    3 would simply be false.
 */

export const THIN_POOL_THRESHOLD = 3;

const clausulaDeFirmeza = {
  en: ' With 3 or more, the estimate is firmer.',
  es: ' Con 3 o más, la estimación es más firme.',
};

/**
 * @param {number} userComps   comparables the user added that reached the ARV
 * @param {number} systemComps automatic comparables that reached the ARV
 * @param {string} lang        'en' | 'es'
 * @returns {string|null} the note, or null when the user added nothing (the caller then
 *                        falls back to the engine's own tier note)
 */
export function arvSourceNote(userComps, systemComps, lang) {
  const n = Number(userComps) || 0;
  const s = Number(systemComps) || 0;
  if (n <= 0) return null;

  const es = lang === 'es';
  let texto;
  if (s > 0) {
    texto = es
      ? `Calculado con ${s} comparable${s === 1 ? '' : 's'} automático${s === 1 ? '' : 's'} más ${n === 1 ? 'el que agregaste' : `los ${n} que agregaste`}.`
      : `Built from ${s} automatic comparable${s === 1 ? '' : 's'} plus the ${n} you added.`;
  } else {
    texto = es
      ? `Calculado con ${n === 1 ? 'el comparable que agregaste' : `los ${n} comparables que agregaste`} — tu evidencia, no el pool automático.`
      : `Built from the ${n} comparable${n === 1 ? '' : 's'} you added — your evidence, not the automatic pool.`;
  }

  return n + s < THIN_POOL_THRESHOLD ? texto + clausulaDeFirmeza[es ? 'es' : 'en'] : texto;
}
